import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, Calendar, MessageSquare, Users, Clock } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    title: '',
    phone: '',
    industry: '',
    message: '',
    contactType: 'demo'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const payload = new FormData();
      payload.append('access_key', '0057fa6e-08f0-4fa9-a8ae-703a52ee97d5');
      payload.append('subject', `CredFlexi Enterprise Request — ${formData.contactType}`);
      payload.append('from_name', 'CredFlexi Website');
      payload.append('name', `${formData.firstName} ${formData.lastName}`);
      payload.append('email', formData.email);
      payload.append('phone', formData.phone);
      payload.append('company', formData.company);
      payload.append('title', formData.title);
      payload.append('industry', formData.industry);
      payload.append('contact_type', formData.contactType);
      payload.append('message', formData.message);

      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: payload,
      });

      const data = await response.json();

      if (data.success) {
        setSubmitMessage('Thank you! Your request has been submitted. Our enterprise team will contact you within 24 hours.');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          company: '',
          title: '',
          phone: '',
          industry: '',
          message: '',
          contactType: 'demo'
        });

        setTimeout(() => setSubmitMessage(''), 8000);
      } else {
        throw new Error(data.message || 'Failed to submit form');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitMessage('Sorry, there was an error. Please try again or email us directly at info@credflexi.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactOptions = [
    {
      type: 'demo',
      title: 'Schedule Enterprise Demo',
      description: 'See CredFlexi in action with a personalized demonstration',
      icon: Calendar
    },
    {
      type: 'consultation',
      title: 'Strategic Consultation',
      description: 'Discuss your specific enterprise requirements',
      icon: Users
    },
    {
      type: 'technical',
      title: 'Technical Integration',
      description: 'Get technical guidance for platform integration',
      icon: MessageSquare
    },
    {
      type: 'support',
      title: 'Enterprise Support',
      description: 'Existing customer support and assistance',
      icon: Phone
    }
  ];

  const industries = [
    'Manufacturing & Industrial',
    'Construction & Building',
    'Healthcare & Medical',
    'Financial Services',
    'Insurance',
    'Retail & E-commerce',
    'Technology',
    'Government',
    'Other'
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#854DFF] to-[#7A42E6] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Contact Enterprise Sales
          </h1>
          <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-4xl mx-auto">
            Ready to transform your enterprise operations? Our experts are standing by to discuss your specific requirements.
          </p>
        </div>
      </section>

      {/* Contact Options */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              How Can We Help You?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Select the type of assistance you need and we'll connect you with the right expert.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {contactOptions.map((option) => {
              const IconComponent = option.icon;
              return (
                <div
                  key={option.type}
                  className={`p-6 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                    formData.contactType === option.type
                      ? 'border-[#854DFF] bg-[#854DFF]/10'
                      : 'border-gray-200 hover:border-[#854DFF] hover:bg-gray-50'
                  }`}
                  onClick={() => setFormData(prev => ({ ...prev, contactType: option.type }))}
                >
                  <div className="flex flex-col items-center text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#854DFF]/10 to-[#854DFF]/20 rounded-xl flex items-center justify-center mb-4">
                      <IconComponent className="w-8 h-8 text-[#854DFF]" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{option.title}</h3>
                    <p className="text-gray-600 text-sm">{option.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border border-gray-100">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Let's Get Started
              </h2>
              <p className="text-gray-600">
                Fill out the form below and our enterprise team will contact you within 24 hours.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name Fields */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#854DFF] focus:border-transparent transition-colors duration-200"
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#854DFF] focus:border-transparent transition-colors duration-200"
                  />
                </div>
              </div>

              {/* Email and Phone */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Business Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#854DFF] focus:border-transparent transition-colors duration-200"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#854DFF] focus:border-transparent transition-colors duration-200"
                  />
                </div>
              </div>

              {/* Company and Title */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#854DFF] focus:border-transparent transition-colors duration-200"
                  />
                </div>
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                    Job Title *
                  </label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#854DFF] focus:border-transparent transition-colors duration-200"
                  />
                </div>
              </div>

              {/* Industry */}
              <div>
                <label htmlFor="industry" className="block text-sm font-medium text-gray-700 mb-2">
                  Industry *
                </label>
                <select
                  id="industry"
                  name="industry"
                  value={formData.industry}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors duration-200"
                >
                  <option value="">Select your industry</option>
                  {industries.map((industry) => (
                    <option key={industry} value={industry}>{industry}</option>
                  ))}
                </select>
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  How can we help you?
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors duration-200"
                  placeholder="Tell us about your specific requirements, current challenges, or questions about our platform..."
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-[#854DFF] to-[#7A42E6] text-white font-semibold py-4 px-6 rounded-lg hover:from-[#7A42E6] hover:to-[#6F37CC] focus:ring-2 focus:ring-[#854DFF]/50 transition-all duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                    Submitting Request...
                  </>
                ) : (
                  <>
                    <Send className="h-5 w-5 mr-3" />
                    Submit Enterprise Request
                  </>
                )}
              </button>

              {/* Success/Error Message */}
              {submitMessage && (
                <div className={`text-center p-4 rounded-lg ${
                  submitMessage.includes('error') 
                    ? 'bg-red-50 text-red-700 border border-red-200' 
                    : 'bg-green-50 text-green-700 border border-green-200'
                }`}>
                  {submitMessage}
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-6">
              Direct Contact Information
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Prefer to reach out directly? Our enterprise sales team is available to assist you.
            </p>
          </div>

          <div className="flex justify-center">
            {/* Single Global Sales Contact */}
            <div className="text-center max-w-md">
              <div className="mx-auto w-16 h-16 bg-gradient-to-br from-[#854DFF] to-[#7A42E6] rounded-xl flex items-center justify-center mb-6">
                <Mail className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">Global Sales</h3>
              <div className="space-y-3 text-gray-300">
                <div className="flex items-center justify-center space-x-2">
                  <Mail className="w-5 h-5" />
                  <a href="mailto:sales@credflexi.com" className="text-lg hover:text-white transition-colors">
                    sales@credflexi.com
                  </a>
                </div>
                <p className="text-sm text-gray-400 mt-4">
                  Available 24/7 to serve you worldwide
                </p>
              </div>
            </div>
          </div>

          <div className="text-center mt-12 pt-8 border-t border-gray-800">
            <p className="text-gray-400 mb-4">
              For general inquiries or existing customer support:
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <a href="mailto:info@credflexi.com" className="flex items-center space-x-2 text-purple-400 hover:text-white transition-colors">
                <Mail className="w-5 h-5" />
                <span>info@credflexi.com</span>
              </a>
              <span className="text-gray-500">•</span>
              <span className="text-gray-400">24/7 Enterprise Support Available</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
